/**
 * Mermaid diagram extraction + lazy hydration.
 *
 * Mermaid fences are pulled out of the raw markdown source BEFORE markdown-it
 * runs and replaced with a placeholder div:
 *
 *   ```mermaid                 →  <div class="mermaid-placeholder"
 *   graph LR                                  data-mermaid-index="0"></div>
 *     A --> B
 *   ```
 *
 * The diagram body is pushed into a `diagrams[]` array on the RenderResult
 * so the consumer (Preview.svelte, Phase 6) can call `hydrateMermaid` after
 * the placeholder lands in the DOM. Mermaid itself is lazy-imported the
 * FIRST time hydrateMermaid runs — no bytes pay for a document with no
 * diagrams, and the import promise is cached so subsequent renders don't
 * pay TLS/parse twice.
 *
 * The placeholder div is plain HTML on the DOMPurify allowlist
 * (`<div class="..." data-mermaid-index="N"></div>`), so it survives the
 * sanitize pass intact. It does need markdown-it `html: true` to make it
 * through markdown-it's block tokenizer — DOMPurify is the real safety
 * boundary, not markdown-it's html flag.
 */

// Match ` ```mermaid\n<body>\n``` ` fences. Non-greedy on the body so two
// diagrams in one document don't collapse into one.
const FENCE_RE = /```mermaid\s*\n([\s\S]*?)\n```/g

export interface ExtractMermaidResult {
  /** Source with mermaid fences replaced by placeholder divs. */
  protected: string
  /** Raw mermaid sources, in document order. Indexed by data-mermaid-index. */
  diagrams: string[]
  /**
   * Per-render nonce stamped on every placeholder. Consumer MUST pass this
   * to hydrateMermaid so spoofed placeholder divs (now possible since
   * markdown-it html:true) can't claim a hydration slot.
   * P1.5 from the 2026-05-23 codex audit.
   */
  nonce: string
}

function makeNonce(): string {
  return Math.random().toString(36).slice(2, 10)
}

export function extractMermaid(source: string): ExtractMermaidResult {
  const diagrams: string[] = []
  const nonce = makeNonce()
  const protectedSource = source.replace(FENCE_RE, (_full, body: string) => {
    const idx = diagrams.length
    diagrams.push(body)
    // data-mermaid-nonce is a stamp the hydrator filters on — a user who
    // pastes the literal placeholder string into their markdown can't
    // forge it because they don't know the per-render value. Both attrs
    // are on the sanitize allowlist.
    return `<div class="mermaid-placeholder" data-mermaid-nonce="${nonce}" data-mermaid-index="${idx}"></div>`
  })
  return { protected: protectedSource, diagrams, nonce }
}

// ---- hydration ---------------------------------------------------------

// Module-scoped cache for the mermaid module + init promise. The first
// hydrateMermaid call lazy-imports mermaid; subsequent calls reuse the
// same Promise (so concurrent renders only fetch + parse once).
let mermaidPromise: Promise<typeof import('mermaid')> | null = null

export type MermaidTheme = 'default' | 'dark' | 'neutral' | 'forest'

// Track the active theme so we know when a re-init+re-render is required.
// Mermaid's initialize() is a singleton — calling it again with a new theme
// updates future renders but doesn't repaint existing SVGs in place. We have
// to mermaid.render() each diagram again after re-init.
let activeTheme: MermaidTheme | null = null

function loadMermaid(theme: MermaidTheme): Promise<typeof import('mermaid')> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => {
      mod.default.initialize({
        startOnLoad: false, // we manage hydration ourselves
        theme,
        // strict = no user-supplied HTML in labels, no script execution.
        // The share-URL flow makes every diagram attacker-controlled, so
        // this is the only acceptable setting.
        securityLevel: 'strict',
        fontFamily: 'inherit',
      })
      activeTheme = theme
      return mod
    })
  } else if (activeTheme !== theme) {
    // P1.10: re-init with the new theme so subsequent renders use it.
    // Existing SVGs in the container will be re-painted by the loop below.
    void mermaidPromise.then((mod) => {
      mod.default.initialize({
        startOnLoad: false,
        theme,
        securityLevel: 'strict',
        fontFamily: 'inherit',
      })
      activeTheme = theme
    })
  }
  return mermaidPromise
}

// Each hydrate call needs unique SVG ids so re-rendering the same diagram
// (preview update on every keystroke) doesn't collide with the previous
// SVG's internal id space.
let renderCounter = 0

export interface HydrateMermaidOptions {
  /** Mermaid theme — defaults to 'default'. Pass 'dark' for dark mode. */
  theme?: MermaidTheme
}

/**
 * Walk a mounted DOM container, find every mermaid-placeholder div whose
 * nonce matches, and replace its content with the rendered SVG. Errors
 * per-diagram fall back to a `mermaid-error` class + the error message as
 * textContent, so one busted diagram doesn't blank the rest of the document.
 *
 * The nonce check (P1.5) prevents user-pasted literal placeholder HTML from
 * spoofing hydration. Without it, html:true + a paste of
 * `<div class="mermaid-placeholder" data-mermaid-index="0"></div>` could
 * trigger a hydration attempt against a non-existent or unrelated diagram.
 *
 * Theme switching (P1.10): callers pass `options.theme` and the hydrator
 * re-initializes mermaid with the new theme before rendering. Because
 * initialize() is a singleton, this also affects future hydrations until
 * the next theme switch.
 *
 * Idempotent: re-running on the same container re-renders every placeholder.
 *
 * SAFETY: We assign `el.innerHTML = svg` where `svg` is produced by mermaid
 * from a source we already control. Mermaid runs at securityLevel:'strict'
 * which strips user HTML from labels (so even attacker-supplied diagram
 * sources can't smuggle <script>). The rendered SVG is trusted; the
 * placeholder content is wiped, so no host-page state leaks across renders.
 */
export async function hydrateMermaid(
  container: HTMLElement,
  diagrams: string[],
  nonce: string,
  options: HydrateMermaidOptions = {},
): Promise<void> {
  if (diagrams.length === 0) return
  // CSS.escape because nonces are base36 but a future change could broaden
  // the alphabet. Filtering by nonce (P1.5) skips spoofed placeholders.
  const selector = `.mermaid-placeholder[data-mermaid-nonce="${CSS.escape(nonce)}"][data-mermaid-index]`
  const placeholders = container.querySelectorAll<HTMLElement>(selector)
  if (placeholders.length === 0) return

  const theme: MermaidTheme = options.theme ?? 'default'
  const mod = await loadMermaid(theme)
  const mermaid = mod.default
  // Wait for any pending re-init to settle so the loop below uses the
  // requested theme — loadMermaid only kicks off the re-init asynchronously.
  if (activeTheme !== theme) {
    mermaid.initialize({
      startOnLoad: false,
      theme,
      securityLevel: 'strict',
      fontFamily: 'inherit',
    })
    activeTheme = theme
  }

  for (const el of placeholders) {
    const idxAttr = el.getAttribute('data-mermaid-index')
    const idx = idxAttr === null ? -1 : Number(idxAttr)
    const source = diagrams[idx]
    if (typeof source !== 'string') continue
    const id = `mermaid-${++renderCounter}`
    try {
      const { svg } = await mermaid.render(id, source)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- SVG trusted, see SAFETY above
      ;(el as HTMLElement).innerHTML = svg
      el.classList.remove('mermaid-error')
    } catch (err) {
      el.classList.add('mermaid-error')
      el.textContent =
        err instanceof Error ? err.message : 'Mermaid render error'
    }
  }
}
